import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGoogleLogin } from '@react-oauth/google';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  CircularProgress,
  Avatar,
  Divider,
  IconButton,
  Tooltip,
  Popper,
  Fade,
  Card,
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Palette as PaletteIcon,
  Google as GoogleIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import themes from '../theme/themes';
import axios from 'axios';

const LoginPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { login, loading } = useAuth();
  const { currentTheme, changeTheme, allThemes } = useTheme();
  const [email, setEmail] = useState('driver@example.com');
  const [password, setPassword] = useState('password123');
  const [error, setError] = useState('');
  const [language, setLanguage] = useState(i18n.language);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openPalette, setOpenPalette] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    const result = await login(email, password);
    if (result.success) {
      navigate('/');
    } else {
      setError(result.error || 'Login failed');
    }
  };

  // تسجيل الدخول عبر Google
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setGoogleLoading(true);
        // الحصول على معلومات المستخدم من Google
        const userInfo = await axios.get('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` }
        });
        
        // إرسال المعلومات إلى الخادم الخلفي
        const response = await axios.post('/api/auth/google', {
          email: userInfo.data.email,
          name: userInfo.data.name,
          googleId: userInfo.data.sub,
          picture: userInfo.data.picture,
        });
        
        const { token, user } = response.data;
        localStorage.setItem('token', token);
        navigate('/');
      } catch (err) {
        setError('فشل تسجيل الدخول عبر Google');
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: () => {
      setError('فشل تسجيل الدخول عبر Google');
    },
  });

  const handleLanguageChange = (event, newLang) => {
    if (newLang) {
      setLanguage(newLang);
      i18n.changeLanguage(newLang);
    }
  };

  const handlePaletteClick = (event) => {
    setAnchorEl(event.currentTarget);
    setOpenPalette((prev) => !prev);
  };

  const handleThemeChange = (themeId) => {
    changeTheme(themeId);
    setOpenPalette(false);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: (theme) => theme.palette.background.default,
        padding: 2,
        position: 'relative',
        overflow: 'hidden',
        transition: 'background 0.5s ease',
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 4,
            background: (theme) => theme.palette.background.paper,
            backdropFilter: 'blur(20px)',
            border: (theme) => `1px solid ${theme.palette.primary.light}40`,
            position: 'relative',
            zIndex: 1,
            transition: 'all 0.5s ease',
          }}
        >
          {/* زر تبديل الثيمات */}
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Tooltip title={t('changeTheme') || 'تغيير الثيم'}>
              <IconButton
                onClick={handlePaletteClick}
                sx={{
                  color: (theme) => theme.palette.primary.main,
                  '&:hover': {
                    background: (theme) => `${theme.palette.primary.main}20`,
                  },
                }}
              >
                <PaletteIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {/* قائمة الثيمات المنبثقة */}
          <Popper
            open={openPalette}
            anchorEl={anchorEl}
            placement="bottom-end"
            transition
            sx={{ zIndex: 9999 }}
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={350}>
                <Card
                  sx={{
                    p: 2,
                    mt: 1,
                    borderRadius: 3,
                    maxWidth: 280,
                    background: (theme) => theme.palette.background.paper,
                    backdropFilter: 'blur(20px)',
                    border: (theme) => `1px solid ${theme.palette.primary.light}40`,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    🎨 اختر الثيم المفضل
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    {Object.values(allThemes).map((themeData) => (
                      <Button
                        key={themeData.id}
                        variant={currentTheme === themeData.id ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => handleThemeChange(themeData.id)}
                        sx={{
                          justifyContent: 'flex-start',
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500,
                          borderColor: (theme) => theme.palette.primary.main,
                          color: currentTheme === themeData.id ? '#FFFFFF' : (theme) => theme.palette.text.primary,
                          background: currentTheme === themeData.id 
                            ? (theme) => `linear-gradient(135deg, ${themeData.colors.primary}, ${themeData.colors.secondary})`
                            : 'transparent',
                          '&:hover': {
                            background: currentTheme === themeData.id 
                              ? (theme) => `linear-gradient(135deg, ${themeData.colors.primary}, ${themeData.colors.secondary})`
                              : (theme) => `${themeData.colors.primary}20`,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            mr: 1.5,
                            background: `linear-gradient(135deg, ${themeData.colors.primary}, ${themeData.colors.secondary})`,
                            border: '2px solid rgba(255,255,255,0.3)',
                          }}
                        />
                        <span dir="ltr">{themeData.icon} {themeData.name}</span>
                      </Button>
                    ))}
                  </Box>
                </Card>
              </Fade>
            )}
          </Popper>

          <Box textAlign="center" mb={3}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                margin: '0 auto',
                background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                boxShadow: (theme) => `0 8px 32px ${theme.palette.primary.main}40`,
                transition: 'all 0.5s ease',
              }}
            >
              <span style={{ fontSize: 40 }}>🚗</span>
            </Avatar>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                mt: 2,
                background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontWeight: 700,
                transition: 'all 0.5s ease',
              }}
            >
              {t('appName')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: (theme) => theme.palette.text.secondary,
                opacity: 0.8,
                fontWeight: 500,
              }}
            >
              {t('subtitle')}
            </Typography>
          </Box>

          <Box display="flex" justifyContent="center" mb={3}>
            <ToggleButtonGroup
              value={language}
              exclusive
              onChange={handleLanguageChange}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  borderRadius: 50,
                  px: 3,
                  borderColor: (theme) => theme.palette.primary.light,
                  color: (theme) => theme.palette.text.primary,
                  '&.Mui-selected': {
                    background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    color: '#FFFFFF',
                  },
                },
              }}
            >
              <ToggleButton value="ar">🇸🇦 {t('arabic')}</ToggleButton>
              <ToggleButton value="en">🇬🇧 {t('english')}</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Divider sx={{ mb: 3, borderColor: (theme) => `${theme.palette.primary.light}40` }} />

          <form onSubmit={handleLogin}>
            <TextField
              fullWidth
              label={t('email')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              InputProps={{
                startAdornment: <EmailIcon sx={{ color: (theme) => theme.palette.primary.light, mr: 1 }} />,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                },
              }}
            />
            <TextField
              fullWidth
              label={t('password')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              InputProps={{
                startAdornment: <LockIcon sx={{ color: (theme) => theme.palette.primary.light, mr: 1 }} />,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                },
              }}
            />

            {error && (
              <Alert
                severity="error"
                sx={{
                  mt: 2,
                  borderRadius: 3,
                  border: '1px solid rgba(211, 47, 47, 0.2)',
                }}
              >
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 3,
                py: 1.5,
                fontSize: 16,
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : t('login')}
            </Button>

            {/* زر تسجيل الدخول عبر Google */}
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Divider sx={{ mb: 2, borderColor: (theme) => `${theme.palette.primary.light}40` }}>
                <Typography variant="caption" color="text.secondary">
                  أو
                </Typography>
              </Divider>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => googleLogin()}
                disabled={googleLoading}
                startIcon={googleLoading ? <CircularProgress size={20} /> : <GoogleIcon />}
                sx={{
                  py: 1.5,
                  borderRadius: 50,
                  borderColor: (theme) => theme.palette.primary.main,
                  color: (theme) => theme.palette.text.primary,
                  '&:hover': {
                    borderColor: (theme) => theme.palette.primary.dark,
                    background: (theme) => `${theme.palette.primary.main}10`,
                  },
                }}
              >
                {googleLoading ? 'جاري الاتصال بـ Google...' : 'تسجيل الدخول عبر Google'}
              </Button>
            </Box>

            <Box textAlign="center" mt={2}>
              <Typography variant="body2" color="text.secondary">
                {t('noAccount')}{' '}
                <Button
                  variant="text"
                  size="small"
		  onClick={() => navigate('/register')}  // أضف هذا السطر
                  sx={{
                    textTransform: 'none',
                    color: (theme) => theme.palette.primary.main,
                    '&:hover': {
                      background: (theme) => `${theme.palette.primary.main}20`,
                    },
                  }}
                >
                  {t('signUp')}
                </Button>
              </Typography>
            </Box>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default LoginPage;
